import type { DustEventMeta } from "./types";

/**
 * How many recent message ids each callback remembers. A mirrored event's two
 * copies arrive within seconds of each other, so this only needs to cover the
 * events one subscriber can receive in that window.
 */
const seenMessageIdLimit = 200;

const extractPusherMessageId = (data: unknown): string | undefined => {
  if (typeof data !== "object" || data === null || Array.isArray(data)) {
    return undefined;
  }

  const messageId = (data as Record<string, unknown>).message_id;
  return typeof messageId === "string" ? messageId : undefined;
};

const withoutMessageId = (data: Record<string, unknown>): Record<string, unknown> => {
  const { message_id: _messageId, ...rest } = data;
  return rest;
};

/**
 * Wraps a realtime callback that is bound on both Pusher and DUST, so an event
 * the backend mirrors onto both transports reaches it once.
 *
 * The backend stamps both copies with the same `message_id`. DUST strips it from
 * the payload and passes it as `meta.messageId`; Pusher leaves it in the payload,
 * so it is removed here before the callback sees it — strict payload validators
 * reject unknown keys. Events without an id (everything published before the
 * mirror, and every Pusher-only event) always pass through.
 *
 * @param callback The subscriber's callback.
 * @return A callback to register on both transports in place of `callback`.
 */
export const createTransportDedupedCallback = <T>(
  callback: (data: T) => unknown
): ((data: unknown, meta?: DustEventMeta) => unknown) => {
  const seenMessageIds = new Set<string>();

  return (data, meta) => {
    const pusherMessageId = extractPusherMessageId(data);
    const messageId = meta?.messageId ?? pusherMessageId;

    if (messageId !== undefined) {
      if (seenMessageIds.has(messageId)) {
        return undefined;
      }

      seenMessageIds.add(messageId);
      if (seenMessageIds.size > seenMessageIdLimit) {
        // Sets iterate in insertion order, so the first entry is the oldest.
        seenMessageIds.delete(seenMessageIds.values().next().value as string);
      }
    }

    return callback(
      (pusherMessageId === undefined ? data : withoutMessageId(data as Record<string, unknown>)) as T
    );
  };
};
